
// Notifications.js - Improved with better animations, positioning and styles
import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

const Notifications = ({ message, type, clearNotification, timeout = 5000 }) => {
  const [visible, setVisible] = useState(false);
  const [exit, setExit] = useState(false);

  useEffect(() => {
    if (message) {
      setVisible(true);
      setExit(false);
      
      // Auto-dismiss notification after timeout
      const timer = setTimeout(() => {
        handleDismiss();
      }, timeout);
      
      return () => clearTimeout(timer);
    }
  }, [message, timeout, clearNotification]);

  const handleDismiss = () => {
    setExit(true);
    setTimeout(() => {
      setVisible(false);
      if (clearNotification) {
        clearNotification();
      }
    }, 300);
  };

  // Return null if no message or if notification is dismissed
  if (!message || !visible) return null;

  // Determine icon and styles based on notification type
  let icon = <Info className="h-5 w-5" />;
  let bgColor = 'bg-blue-50 dark:bg-blue-900/20';
  let borderColor = 'border-blue-500';
  let textColor = 'text-blue-800 dark:text-blue-200';
  let iconColor = 'text-blue-500';

  switch (type) {
    case 'success':
      icon = <CheckCircle className="h-5 w-5" />;
      bgColor = 'bg-green-50 dark:bg-green-900/20';
      borderColor = 'border-green-500';
      textColor = 'text-green-800 dark:text-green-200';
      iconColor = 'text-green-500';
      break;
    case 'error':
      icon = <AlertCircle className="h-5 w-5" />;
      bgColor = 'bg-red-50 dark:bg-red-900/20';
      borderColor = 'border-red-500';
      textColor = 'text-red-800 dark:text-red-200';
      iconColor = 'text-red-500';
      break;
    case 'warning':
      icon = <AlertCircle className="h-5 w-5" />;
      bgColor = 'bg-yellow-50 dark:bg-yellow-900/20';
      borderColor = 'border-yellow-500';
      textColor = 'text-yellow-800 dark:text-yellow-200';
      iconColor = 'text-yellow-500';
      break;
    default:
      // info style is default (set above)
      break;
  }

  return (
    <div 
      className={`fixed top-4 right-4 z-50 max-w-md transform transition-all duration-300 ${
        exit ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
      }`}
    >
      <div className={`${bgColor} border-l-4 ${borderColor} p-4 rounded-lg shadow-lg backdrop-blur-sm`}>
        <div className="flex items-center">
          <div className={`flex-shrink-0 ${iconColor}`}>
            {icon}
          </div>
          <div className="ml-3 flex-grow">
            <p className={`text-sm font-medium ${textColor}`}>{message}</p>
          </div>
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                className={`inline-flex rounded-full p-1.5 ${textColor} hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${type === 'info' ? 'blue' : type === 'success' ? 'green' : type === 'error' ? 'red' : 'yellow'}-500`}
                onClick={handleDismiss}
              >
                <span className="sr-only">Dismiss</span>
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;